import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { createOrder, verifyPayment, getConfig, validateCoupon } from "../lib/api";
import { toast } from "sonner";

const loadRazorpayScript = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement("script");
  script.src = "https://checkout.razorpay.com/v1/checkout.js";
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "Hyderabad", pincode: "", notes: "" });
  const [config, setConfig] = useState({ razorpay_enabled: false });
  const [loading, setLoading] = useState(false);
  const [placed, setPlaced] = useState(false);
  const [coupon, setCoupon] = useState({ code: "", discount: 0 });
  const [couponInput, setCouponInput] = useState("");

  // Shipping: flat ₹89, waived when subtotal >= ₹2999 (matches backend logic)
  const SHIPPING_FEE = 89;
  const FREE_SHIPPING_THRESHOLD = 2999;
  const shipping = items.length === 0 ? 0 : (total >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE);
  const remainingForFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - total);
  const finalTotal = Math.max(total - coupon.discount, 0) + shipping;

  useEffect(() => { getConfig().then(setConfig).catch(() => {}); }, []);
  useEffect(() => { if (!placed && items.length === 0) navigate("/cart"); }, [items, navigate, placed]);

  // Prefill from logged-in user + most recent saved address
  useEffect(() => {
    if (!user) return;
    const a = (user.addresses || [])[0];
    setForm((f) => ({
      ...f,
      name: f.name || user.name || "",
      email: f.email || user.email || "",
      phone: f.phone || user.phone || "",
      address: f.address || (a?.address || ""),
      city: f.city || (a?.city || "Hyderabad"),
      pincode: f.pincode || (a?.pincode || ""),
    }));
  }, [user]);

  // Abandoned cart tracking: capture on unmount if user typed contact info but didn't place order
  useEffect(() => {
    return () => {
      if (placed || items.length === 0) return;
      if (!form.name && !form.email && !form.phone) return;
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/abandoned-cart`;
        const body = JSON.stringify({
          items: items.map(({ product_id, name, price, quantity, variant_id, variant_color_name }) => ({ product_id, name, price, quantity, variant_id: variant_id || null, variant_color_name: variant_color_name || null })),
          customer: form,
          total,
        });
        navigator.sendBeacon ? navigator.sendBeacon(url, new Blob([body], { type: "application/json" }))
          : fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed, items, form]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const f of ["name","email","phone","address","city","pincode"]) {
      if (!form[f]) return toast.error(`Please enter your ${f}`);
    }
    setLoading(true);
    try {
      const payload = {
        items: items.map(({ product_id, name, price, quantity, image, note, variant_id, variant_color_name, variant_color_hex }) => ({ product_id, name, price, quantity, image, note: note || "", variant_id: variant_id || null, variant_color_name: variant_color_name || null, variant_color_hex: variant_color_hex || null })),
        customer: form,
        payment_method: config.razorpay_enabled ? "razorpay" : "cod",
        coupon_code: coupon.code || "",
      };
      const res = await createOrder(payload);
      const orderId = res.order.id;

      if (res.razorpay_order && config.razorpay_enabled) {
        const loaded = await loadRazorpayScript();
        if (!loaded) { toast.error("Failed to load payment gateway"); setLoading(false); return; }
        const rzp = new window.Razorpay({
          key: res.razorpay_key_id,
          amount: res.razorpay_order.amount,
          currency: "INR",
          name: "Naathi Charami Gifts & Hampers",
          description: "Order Payment",
          order_id: res.razorpay_order.id,
          prefill: { name: form.name, email: form.email, contact: form.phone },
          theme: { color: "#7A1F2B" },
          handler: async (resp) => {
            try {
              await verifyPayment({
                order_id: orderId,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              setPlaced(true);
              navigate(`/order-success/${orderId}`, { replace: true });
              clear();
              toast.success("Payment successful!");
            } catch (err) {
              toast.error("Payment verification failed");
            }
          },
        });
        rzp.open();
      } else {
        // COD / placeholder
        await verifyPayment({ order_id: orderId });
        setPlaced(true);
        navigate(`/order-success/${orderId}`, { replace: true });
        clear();
        toast.success("Order placed! We'll contact you on WhatsApp.");
      }
    } catch (err) {
      // Surface the real backend reason instead of a generic "Failed to place order"
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      let msg;
      if (detail) {
        msg = typeof detail === "string" ? detail : JSON.stringify(detail);
      } else if (status === 500) {
        msg = "Payment gateway error — please try again or contact support";
      } else if (err?.message === "Network Error") {
        msg = "Network error — please check your connection";
      } else {
        msg = err?.message || "Failed to place order";
      }
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error("Order creation failed:", { status, detail, err });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-20" data-testid="checkout-page">
      <div className="container-x">
        <p className="label-small">Final Step</p>
        <h1 className="font-serif text-5xl text-[#2D2424] mt-3">Checkout</h1>

        <form onSubmit={handleSubmit} className="mt-12 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-5">
            <h3 className="font-serif text-2xl text-[#2D2424]">Delivery Details</h3>
            {[
              { k: "name", label: "Full Name", ac: "name" },
              { k: "email", label: "Email", type: "email", ac: "email" },
              { k: "phone", label: "Phone (for WhatsApp updates)", type: "tel", ac: "tel" },
              { k: "address", label: "Address", ac: "street-address" },
              { k: "city", label: "City", ac: "address-level2" },
              { k: "pincode", label: "Pincode", ac: "postal-code" },
              { k: "notes", label: "Notes / Personalized message (optional)", ac: "off" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-xs tracking-wider uppercase text-[#5C5555]">{f.label}</label>
                <input
                  type={f.type || "text"}
                  autoComplete={f.ac}
                  value={form[f.k]}
                  onChange={onChange(f.k)}
                  data-testid={`checkout-${f.k}`}
                  className="mt-1 w-full border border-[#E5DBD8] px-4 py-3 bg-white focus:outline-none focus:border-[#7A1F2B]"
                />
              </div>
            ))}
          </div>

          <div className="bg-[#F2E8E4]/50 border border-[#E5DBD8] p-6 h-fit">
            <h3 className="font-serif text-2xl text-[#2D2424]">Order Summary</h3>
            <div className="mt-5 space-y-3">
              {items.map((it) => (
                <div key={it.product_id} className="text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#5C5555]">{it.name} × {it.quantity}</span>
                    <span>₹{(it.price * it.quantity).toLocaleString("en-IN")}</span>
                  </div>
                  {it.note && (
                    <p className="mt-1 text-xs italic text-[#7A1F2B] font-hand">💌 &ldquo;{it.note}&rdquo;</p>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-[#E5DBD8]">
              {/* First-order sweetener: free handwritten note reminder */}
              {user && user.first_order_note && (
                <div
                  className="mb-4 p-3 bg-[#FFE7F1] border-2 border-dashed border-[#FF6B9D] rounded-xl -rotate-1"
                  style={{ boxShadow: "3px 3px 0 #FF6B9D" }}
                  data-testid="first-order-note-banner"
                >
                  <p className="font-doodle text-sm sm:text-base text-[#7A1F2B] leading-tight">
                    🎁 FREE handwritten note on us!
                  </p>
                  <p className="font-hand text-xs text-[#5C5555] mt-1">
                    Your first order includes a hand-lettered message — just add it above.
                  </p>
                </div>
              )}
              <label className="text-xs tracking-wider uppercase text-[#5C5555]">Coupon Code</label>
              <div className="flex gap-2 mt-1">
                <input
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  data-testid="coupon-input"
                  placeholder="WELCOME10"
                  className="flex-1 border border-[#E5DBD8] px-3 py-2 bg-white text-sm focus:outline-none focus:border-[#7A1F2B]"
                />
                <button
                  type="button"
                  data-testid="coupon-apply"
                  onClick={async () => {
                    if (!couponInput) return;
                    try {
                      const c = await validateCoupon(couponInput, total);
                      setCoupon({ code: c.code, discount: c.discount });
                      toast.success(`Coupon applied: −₹${c.discount}`);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Invalid coupon");
                    }
                  }}
                  className="btn-secondary !py-2 !px-4 !text-[10px]"
                >Apply</button>
              </div>
              {coupon.code && (
                <p className="text-xs text-green-700 mt-2">✓ {coupon.code} applied — saved ₹{coupon.discount}</p>
              )}
            </div>

            <div className="border-t border-[#E5DBD8] my-5"></div>
            <div className="flex justify-between text-sm"><span className="text-[#5C5555]">Subtotal</span><span>₹{total.toLocaleString("en-IN")}</span></div>
            {coupon.discount > 0 && (
              <div className="flex justify-between text-sm mt-1"><span className="text-[#5C5555]">Discount</span><span className="text-green-700">−₹{coupon.discount.toLocaleString("en-IN")}</span></div>
            )}
            <div className="flex justify-between text-sm mt-1" data-testid="checkout-shipping-row">
              <span className="text-[#5C5555]">Shipping</span>
              <span>
                {shipping === 0 ? <span className="text-green-700 font-semibold">FREE</span> : `₹${shipping.toLocaleString("en-IN")}`}
              </span>
            </div>
            {shipping > 0 && (
              <p
                className="mt-2 text-[11px] font-hand text-[#7A1F2B] text-right"
                data-testid="checkout-free-shipping-nudge"
              >
                ✨ Add ₹{remainingForFreeShipping.toLocaleString("en-IN")} more for FREE shipping!
              </p>
            )}
            <div className="flex justify-between font-serif text-xl mt-3 pt-3 border-t border-[#E5DBD8]">
              <span>Total</span>
              <span className="text-[#7A1F2B]" data-testid="checkout-total">₹{finalTotal.toLocaleString("en-IN")}</span>
            </div>
            <p className="text-xs text-[#5C5555] mt-4">
              {config.razorpay_enabled ? "Secure payment via Razorpay" : "Pay on Delivery — confirmation on WhatsApp"}
            </p>
            <button type="submit" disabled={loading} data-testid="checkout-place-order" className="btn-primary w-full mt-6">
              {loading ? "Processing…" : config.razorpay_enabled ? "Pay Securely" : "Place Order"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
